import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { signup, signInWithGoogle } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { Logo } from '@/components/Logo'

export function SignupPage() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const navigate = useNavigate()
  const { showToast } = useToast()

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Google sign-up failed', 'error')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!fullName || !email || !password) {
      showToast('Please fill in all fields', 'error')
      return
    }

    if (password !== confirmPassword) {
      showToast('Passwords do not match', 'error')
      return
    }

    if (password.length < 6) {
      showToast('Password must be at least 6 characters', 'error')
      return
    }

    setLoading(true)

    try {
      await signup(email, password, { fullName })
      showToast('Account created! Please check your email to verify.')
      navigate('/login')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Signup failed', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Left Branding Section */}
      <section className="relative hidden lg:flex flex-col justify-between w-[45%] p-12 text-white overflow-hidden bg-gradient-to-br from-[#4f46e5] to-[#312e81]">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-80 h-80 bg-indigo-400/20 rounded-full blur-3xl" />

        {/* Logo */}
        <div className="relative z-10">
          <Logo size="lg" variant="light" />
        </div>

        {/* Features */}
        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl font-bold leading-[1.2] mb-6">
            Start for free, <br />
            <span className="text-indigo-200">scale as you grow.</span>
          </h2>
          <ul className="space-y-4">
            <li className="flex items-center gap-3 text-indigo-100">
              <span className="material-symbols-outlined text-green-300">check_circle</span>
              50 links per month on free plan
            </li>
            <li className="flex items-center gap-3 text-indigo-100">
              <span className="material-symbols-outlined text-green-300">check_circle</span>
              Basic click analytics included
            </li>
            <li className="flex items-center gap-3 text-indigo-100">
              <span className="material-symbols-outlined text-green-300">check_circle</span>
              No credit card required
            </li>
            <li className="flex items-center gap-3 text-indigo-100">
              <span className="material-symbols-outlined text-green-300">check_circle</span>
              Upgrade anytime for more features
            </li>
          </ul>
        </div>

        {/* Trust Badges */}
        <div className="relative z-10 flex gap-8 text-indigo-200 text-sm">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">groups</span>
            <span>10,000+ Users</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">bolt</span>
            <span>99.9% Uptime</span>
          </div>
        </div>
      </section>

      {/* Right Form Section */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-24 bg-white dark:bg-[#0f172a]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-12">
            <Logo size="lg" variant="auto" />
          </div>

          {/* Header */}
          <div className="mb-10 text-left">
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
              Create Account
            </h1>
            <p className="text-slate-500 dark:text-slate-400">Start shortening links in seconds.</p>
          </div>

          {/* Google Sign Up */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            className="w-full h-12 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl font-semibold text-sm text-slate-700 dark:text-slate-200 transition-all hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-3 mb-6"
          >
            {googleLoading ? (
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
              </svg>
            )}
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            <span className="text-sm text-slate-400">or continue with email</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="w-full h-12 px-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 transition-all"
                autoComplete="name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full h-12 px-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 transition-all"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full h-12 px-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 transition-all"
                autoComplete="new-password"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Confirm Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full h-12 px-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 transition-all"
                autoComplete="new-password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#4f46e5] hover:bg-[#4338ca] text-white rounded-xl font-semibold text-sm transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Sign in link */}
          <p className="mt-8 text-center text-slate-600 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#4f46e5] hover:underline font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}
